// src/frontend/js/modern/modules/wishlist/actions.js

import { BaseModule } from '../../shared/module-base.js';
import { eventBus, EVENTS } from '../../core/event-bus.js';
import { queueManager } from '../../core/queue.js';
import { conflictManager } from '../../core/conflict-manager.js';
import wishlistModuleStore from './store.js';
import wishlistUI from './ui.js';
import { showError, showSuccess } from '../../shared/ui-helpers.js';
import { loadAutomations } from '../../shared/api-helpers.js';
import { ScopeHelper } from '../../shared/scope-helpers.js';
import { ensurejQuery } from "../../shared/ui-helpers";
import { fragmentManager } from '../../core/fragment-manager.js';

class WishlistActions extends BaseModule {
	constructor() {
		super('wishlist', wishlistModuleStore, null, wishlistUI);
	}

	_getIsMerge() {
		return window.wlfmc_l10n?.merge_lists === 'true';
	}

	_getIsMergeSaveForLater() {
		return (
			window.wlfmc_l10n?.merge_save_for_later === true ||
			window.wlfmc_l10n?.merge_save_for_later === '1' ||
			window.wlfmc_l10n?.merge_save_for_later === 'true'
		);
	}

	async add(productId, formData, options = {}) {
		const isMerge = this._getIsMerge();
		const scope = ScopeHelper.wishlist(productId, options.wishlistId || null, isMerge);
		const actionId = `wishlist_add_${productId}_${Date.now()}`;

		// Track the optimistic success toast element so it can be
		// dismissed by rollbackFn if the server rejects the operation.
		let successToastEl = null;

		return this.executeWithConflictManagement(
			scope,
			actionId,
			() => {
				// Optimistic state mutation
				this._optimisticAdd(productId, options.data || {});
				if (options.element) {
					this.ui.showWishlistPopupIfNeeded(options.element, productId);
				} else {
					// Optimistic toast — shown BEFORE server response
					successToastEl = showSuccess(window.wlfmc_l10n?.labels?.product_added_text);
				}
			},
			() => {
				this._optimisticRemove(productId);
				// Dismiss the optimistic success toast on rollback
				if (successToastEl && window.wlfmc_toastr) {
					window.wlfmc_toastr.clear(successToastEl, { force: true });
					successToastEl = null;
				}
			},
			() => queueManager.addWithDeduplication('add_to_wishlist', formData),
			(result) => this._handleAddSuccess(productId, result),
			(error) => this._handleAddError(productId, error),
			{
				type: 'add',
				productId,
				listId: options.wishlistId,
				element: options?.element,
				onDataOnly: (result) => {
					if (result) {
						conflictManager.storeIdentity(scope, {
							item_id: result.item_id || null,
							wishlist_id: result.wishlist_id || null,
							wishlist_token: result.wishlist_token || '',
							remove_nonce: result.nonce || result.remove_nonce || '',
						});
					}
				}
			}
		);
	}

	async remove(productId, wishlistId = 0, wishlistToken = '', nonce = '', element = null) {
		const isMerge = this._getIsMerge();
		const scope = ScopeHelper.wishlist(productId, wishlistId, isMerge);
		const actionId = `wishlist_remove_${productId}_${Date.now()}`;

		let row = null;
		if (element) {
			row = element.closest('[data-row-id]');
			if (row) {
				row.classList.add('disabled-row');
				row.setAttribute('aria-busy', 'true');
			}
			element.setAttribute('disabled', 'true');
		}

		const snapshot = this.snapshotItem(productId);

		return this.executeWithConflictManagement(
			scope,
			actionId,
			() => this._optimisticRemove(productId),
			() => {
				this.restoreSnapshot(snapshot, productId);
				if (row) row.classList.remove('disabled-row');
			},
			() => {
				const identity = conflictManager.getIdentity(scope);
				const resolvedWishlistId = identity?.wishlist_id || wishlistId;
				const resolvedToken = identity?.wishlist_token || wishlistToken;
				const resolvedNonce = identity?.remove_nonce || nonce;
				if (identity) conflictManager.clearIdentity(scope);

				return queueManager.addWithDeduplication('remove_from_wishlist', {
					product_id: productId,
					remove_from_wishlist: productId,
					wishlist_id: resolvedWishlistId,
					wishlist_token: resolvedToken,
					nonce: resolvedNonce,
					_scope: scope,
				});
			},
			(result) => {
				this._handleRemoveSuccess(productId, result, element, row);
			},
			(error) => {
				this._handleRemoveError(productId, error);
				if (row) row.classList.remove('disabled-row');
			},
			{ type: 'remove', productId, listId: wishlistId, element: element }
		);
	}

	async deleteByItemId(productId, itemId, wishlistId = 0, nonce = '', element) {
		const isMerge = this._getIsMerge();
		const scope = ScopeHelper.wishlist(productId, wishlistId, isMerge);
		const actionId = `wishlist_delete_${productId}_${Date.now()}`;
		const row = element?.closest('[data-row-id]');
		const snapshot = this.snapshotItem(productId);

		// Track the optimistic removal toast so it can be dismissed on rollback.
		let removedToastEl = null;

		return this.executeWithConflictManagement(
			scope,
			actionId,
			() => {
				this._optimisticRemove(productId);
				// Optimistic toast — shown BEFORE server response
				if (window.wlfmc_l10n?.labels?.product_removed_text) {
					removedToastEl = showError(window.wlfmc_l10n.labels.product_removed_text);
				}
			},
			() => {
				this.restoreSnapshot(snapshot, productId);
				if (row) row.classList.remove('disabled-row');
				// Dismiss the optimistic removal toast on rollback
				if (removedToastEl && window.wlfmc_toastr) {
					window.wlfmc_toastr.clear(removedToastEl, { force: true });
					removedToastEl = null;
				}
			},
			() => {
				const identity = conflictManager.getIdentity(scope);
				const resolvedWishlistId = identity?.wishlist_id || wishlistId;
				const resolvedItemId = identity?.item_id || itemId;
				const resolvedNonce = identity?.remove_nonce || nonce;
				if (identity) conflictManager.clearIdentity(scope);

				return queueManager.addWithDeduplication('delete_item', {
					product_id: productId,
					remove_from_wishlist: productId,
					wishlist_id: resolvedWishlistId,
					item_id: resolvedItemId,
					nonce: resolvedNonce,
					_scope: scope,
				});
			},
			(result) => {
				this._handleRemoveSuccess(productId, result, element, row);
			},
			(error) => {
				this._handleRemoveError(productId, error);
			},
			{ type: 'delete', productId, listId: wishlistId, element: element }
		);
	}

	async counterRemove(productId, wishlistId = 0, nonce = '', wishlistToken = '', element = null) {
		const isMerge = this._getIsMerge();
		const scope = ScopeHelper.wishlist(productId, wishlistId, isMerge);
		const actionId = `wishlist_counter_remove_${productId}_${Date.now()}`;
		const snapshot = this.snapshotItem(productId);

		return this.executeWithConflictManagement(
			scope,
			actionId,
			() => this._optimisticRemove(productId),
			() => this.restoreSnapshot(snapshot, productId),
			() => {
				const identity = conflictManager.getIdentity(scope);
				const resolvedWishlistId = identity?.wishlist_id || wishlistId;
				const resolvedToken = identity?.wishlist_token || wishlistToken;
				const resolvedNonce = identity?.remove_nonce || nonce;
				if (identity) conflictManager.clearIdentity(scope);

				return queueManager.addWithDeduplication('remove_from_wishlist', {
					product_id: productId,
					remove_from_wishlist: productId,
					wishlist_id: resolvedWishlistId,
					wishlist_token: resolvedToken,
					merge_lists: isMerge,
					nonce: resolvedNonce,
					_scope: scope,
				});
			},
			(result) => this._handleRemoveSuccess(productId, result, element, null),
			(error) => this._handleRemoveError(productId, error),
			{ type: 'remove', productId, listId: wishlistId, element: element }
		);
	}

	/* ═══════════════════════════════════════════
	   OPTIMISTIC — State mutations ONLY.
	   Toast is shown here (optimistic) and tracked
	   so rollbackFn can dismiss it on failure.
	════════════════════════════════════════════ */

	_optimisticAdd(productId, data = {}) {
		if (this.store.hasItem(productId)) {
			this.store.updateItem(productId, { ...data, _optimistic: true, _timestamp: Date.now() });
		} else {
			this.store.addItem(productId, data);
		}
		this.ui.updateAddButton(productId, { item_id: data.item_id || null, wishlist_id: data.wishlist_id || null });
	}

	_optimisticRemove(productId) {
		try {
			const pid = parseInt(productId, 10);
			if (this.store.hasItem(pid)) {
				this.store.removeItem(pid);
			}
			this.ui.updateRemoveButton(pid);
		} catch (error) {
			console.error('[Wishlist] Optimistic remove failed:', error);
		}
	}

	/* ═══════════════════════════════════════════
	   SUCCESS / ERROR HANDLERS
	   All toast messages come from wlfmc_l10n.labels
	   (server-localized) or from error.message which
	   carries the server-translated message.
	════════════════════════════════════════════ */

	_handleAddSuccess(productId, result) {
		if (!result) return;

		// Server returned 'exists' — product is already in the wishlist.
		// The optimistic success toast was already shown; now show the
		// "already in wishlist" toast so the user understands (matches legacy).
		if (result.result === 'exists') {
			const alreadyText = window.wlfmc_l10n?.labels?.already_in_wishlist_text;
			if (alreadyText) {
				showError(alreadyText);
			}
			eventBus.emit(EVENTS.WISHLIST_ADDED, { productId, status: 'already_exists', data: result });
			return;
		}

		// Server returned 'true' — success toast already shown optimistically.

		// Redirect after add (matches legacy click_behavior).
		if (window.wlfmc_l10n?.click_behavior === 'add-redirect' && window.wlfmc_l10n?.wishlist_page_url) {
			window.location.href = window.wlfmc_l10n.wishlist_page_url;
		}

		if (result.item_id) {
			if (result.display) {
				this.store.setDisplayData(productId, result.display);
			}
			this.store.confirmAdd(productId, result);
			this.ui.updateAddButton(productId, { item_id: result.item_id, wishlist_id: result.wishlist_id });

			const jQuery = window.jQuery;
			if (jQuery) {
				const button = document.querySelector(`.wlfmc_add_to_wishlist[data-product-id="${productId}"]`);
				const wrapper = document.querySelector(`.wlfmc-add-to-wishlist-${productId}`);
				jQuery(document.body).trigger('wlfmc_added_to_wishlist', [ensurejQuery(button), ensurejQuery(wrapper)]);
			}

			if (result.wishlist_id && result.customer_id && result.load_automation_nonce) {
				loadAutomations(productId, result.wishlist_id, result.customer_id, 'wishlist', result.load_automation_nonce);
			}
		}

		eventBus.emit(EVENTS.WISHLIST_ADDED, { productId, status: 'success', data: result });
	}

	_handleAddError(productId, error) {
		// error.message carries the server-translated message from the response.
		showError(error.message || window.wlfmc_l10n?.labels?.operation_failed);
		eventBus.emit(EVENTS.WISHLIST_ADDED, { productId, status: 'failed', error: error.message });
	}

	_handleRemoveSuccess(productId, result, element = null, row = null) {
		// Defensive: guarantee removal even if optimistic phase was skipped.
		if (this.store.hasItem(productId)) {
			this.store.removeItem(productId);
		}
		this.ui.updateRemoveButton(productId);

		// Reload table fragment from server to remove the disabled row entirely.
		fragmentManager.loadFragments();
		const jQuery = window.jQuery;
		if (jQuery) {
			jQuery(document.body).trigger('wlfmc_removed_from_wishlist', [ensurejQuery(element), ensurejQuery(row), result]);
		}

		// Server may return an informational message (matches legacy behavior).
		if (result.message) {
			showError(result.message);
		}

		eventBus.emit(EVENTS.WISHLIST_REMOVED, { productId, status: 'success', data: result });
	}

	_handleRemoveError(productId, error) {
		// error.message carries the server-translated message from the response.
		showError(error.message || window.wlfmc_l10n?.labels?.operation_failed);
		eventBus.emit(EVENTS.WISHLIST_REMOVED, { productId, status: 'failed', error: error.message });
	}

	isInWishlist(productId) {
		return this.store.hasItem(productId);
	}
}

export const wishlistActions = new WishlistActions();
export default wishlistActions;
